import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import { debounce } from 'lodash';
import { googleAutoCompleteApi } from 'src/common/Scraper';

export interface BusinessProfileOption {
  name: string;
  address: string;
  placeId: string;
  isSkeleton?: boolean;
}

interface BusinessProfileAutocompleteContextValue {
  inputValue: string;
  selectedOption: BusinessProfileOption | null;
  businessProfiles: BusinessProfileOption[];
  loading: boolean;
  displayedOptions: BusinessProfileOption[];
  setInputValue: (value: string) => void;
  setSelectedOption: (option: BusinessProfileOption | null) => void;
  handleInputChange: (event: any, newInputValue: string) => void;
  handleChange: (event: any, newValue: BusinessProfileOption | null) => void;
  resetBusinessProfileAutocomplete: () => void;
}

const BusinessProfileAutocompleteContext =
  createContext<BusinessProfileAutocompleteContextValue | null>(null);

export const BusinessProfileAutocompleteProvider = (props: {
  children: ReactNode;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<BusinessProfileOption | null>(null);
  const [businessProfiles, setBusinessProfiles] = useState<
    BusinessProfileOption[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Generate skeleton options for loading state
  const skeletonOptions = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, i) => ({
        isSkeleton: true,
        name: '',
        address: '',
        placeId: `skeleton-${i}`
      })),
    []
  );

  // API call function
  const callGoogleAutoCompleteApi = useCallback(
    async (newInputValue: string) => {
      const radiusInMeters = 0.3 * 1000;

      try {
        const response: any = await googleAutoCompleteApi(
          newInputValue,
          radiusInMeters
        );
        const suggestions = response?.suggestions || [];

        const formattedProfiles = suggestions.map((s: any, index: number) => {
          const p = s?.placePrediction;

          return {
            name: p?.structuredFormat?.mainText?.text || p?.text?.text || '',
            address: p?.structuredFormat?.secondaryText?.text || '',
            placeId: p?.placeId || p?.place || `profile-${Date.now()}-${index}`
          };
        });

        setBusinessProfiles(formattedProfiles);
      } catch (error) {
        console.error('Google API Error:', error);
        setBusinessProfiles([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Debounced version of the API call
  const debouncedFetch = useMemo(
    () => debounce(callGoogleAutoCompleteApi, 1200),
    [callGoogleAutoCompleteApi]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  // Handle input change
  const handleInputChange = useCallback(
    (event: any, newInputValue: string) => {
      setInputValue(newInputValue);

      if (!newInputValue) {
        setSelectedOption(null);
        setBusinessProfiles([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      debouncedFetch(newInputValue);
    },
    [debouncedFetch]
  );

  // Handle option selection change
  const handleChange = useCallback(
    (event: any, newValue: BusinessProfileOption | null) => {
      setSelectedOption(newValue);
    },
    []
  );

  const resetBusinessProfileAutocomplete = useCallback(() => {
    setInputValue('');
    setSelectedOption(null);
    setBusinessProfiles([]);
    setLoading(false);
  }, []);

  // Compute displayed options
  const displayedOptions = useMemo(() => {
    if (inputValue?.length === 0 || inputValue === '') {
      return [];
    }
    return loading ? skeletonOptions : businessProfiles;
  }, [loading, businessProfiles, skeletonOptions, inputValue]);

  const value: BusinessProfileAutocompleteContextValue = {
    inputValue,
    selectedOption,
    businessProfiles,
    loading,
    displayedOptions,
    setInputValue,
    setSelectedOption,
    handleInputChange,
    handleChange,
    resetBusinessProfileAutocomplete
  };

  return (
    <BusinessProfileAutocompleteContext.Provider value={value}>
      {props.children}
    </BusinessProfileAutocompleteContext.Provider>
  );
};

export const useBusinessProfileAutocomplete = () => {
  const context = useContext(BusinessProfileAutocompleteContext);
  if (!context) {
    throw new Error(
      'useBusinessProfileAutocomplete must be used within BusinessProfileAutocompleteProvider'
    );
  }
  return context;
};
